import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Share2, Pencil, Lock } from 'lucide-react';
import PublicationDesignRenderer from '@/components/PublicationDesignRenderer';
import PublicationEditor from '@/components/PublicationEditor';
import { PdfPageReader } from '@/components/PdfPageReader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicationReader({ adminPreview = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(adminPreview);
  const [agreementMode, setAgreementMode] = useState('every_time');

  useEffect(() => {
    loadPublication();
    if (!adminPreview) {
      loadAgreementConfig();
    }
    
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable Ctrl+P (print)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+S (save)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+C (copy)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        return false;
      }
      // Disable F12 (dev tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+Shift+I (dev tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [id]);

  const loadAgreementConfig = async () => {
    try {
      const res = await axios.get(`${API}/publication-agreement/config`, { withCredentials: true });
      setAgreementMode(res.data.mode);
      if (res.data.mode === 'once_per_user' && res.data.accepted) {
        setAgreed(true);
      }
    } catch (e) {
      // Default to showing the gate (every_time) on any error
      setAgreementMode('every_time');
    } finally {
      setAgreementChecked(true);
    }
  };

  const handleAgree = async () => {
    setAgreed(true);
    if (agreementMode === 'once_per_user') {
      try {
        await axios.post(`${API}/publication-agreement/accept`, {}, { withCredentials: true });
      } catch (e) {
        console.error('Failed to record agreement:', e);
      }
    }
  };

  const loadPublication = async () => {
    try {
      const endpoint = adminPreview
        ? `${API}/admin/publications/${id}/preview`
        : `${API}/publications/${id}/read`;
      const res = await axios.get(endpoint, { withCredentials: true });
      setPublication(res.data);
    } catch (error) {
      console.error('Failed to load publication:', error);
      if (error.response?.status === 403) {
        setError('Access denied. Please purchase this publication or subscribe.');
      } else if (error.response?.status === 401) {
        navigate('/');
      } else {
        setError(error.response?.data?.detail || 'Failed to load publication');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareText = `Check out "${publication?.title}" on AcuErnie`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: publication?.title,
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5A3C] mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-red-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/publication-center')}
            className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transition-all"
          >
            Browse Publications
          </button>
        </div>
      </Layout>
    );
  }

  if (!publication) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-gray-600">Publication not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Sticky Header */}
        <div className="sticky top-0 z-50 bg-white border-b-4 border-[#1B4B5A] shadow-lg">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <button
              data-testid="reader-back-button"
              onClick={() => navigate(adminPreview ? '/admin/publication-vault' : '/my-publications')}
              className="flex items-center gap-2 text-[#1B4B5A] hover:text-[#B5813B] font-bold transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {adminPreview ? 'Back to Vault' : 'Back to My Publications'}
            </button>

            {adminPreview && (
              <div className="flex items-center gap-3">
                {publication.sections && publication.sections.length > 0 && !editing && (
                  <button
                    data-testid="reader-edit-button"
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-2 bg-[#B5813B] text-white px-4 py-2 rounded-lg font-bold hover:bg-[#9c6f31] transition-all"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <span data-testid="reader-preview-badge" className="px-3 py-1 rounded-full text-sm font-bold bg-[#FDF6EC] text-[#B5813B] border border-[#B5813B]">
                  Admin Preview{publication.is_published ? '' : ' • Draft'}
                </span>
              </div>
            )}

            {/* Only show share for free publications */}
            {(!adminPreview && publication.pricing_type === 'free' && !publication.free_trial) && (
              <button
                onClick={handleShare}
                className="flex items-center gap-2 bg-gradient-to-r from-[#1B4B5A] to-[#0D1117] text-white px-6 py-2 rounded-lg font-bold hover:shadow-xl transition-all"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
            )}
          </div>
        </div>

        {/* Publication Content */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto">
            {!adminPreview && !agreementChecked ? (
              <div className="text-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1B4B5A] mx-auto"></div>
              </div>
            ) : !adminPreview && !agreed ? (
              <PublicationAgreementGate
                title={publication.title}
                onAgree={handleAgree}
                onDecline={() => navigate('/my-publications')}
              />
            ) : editing ? (
              <PublicationEditor
                publication={publication}
                onSaved={() => { setEditing(false); loadPublication(); }}
                onCancel={() => setEditing(false)}
                onPublished={() => navigate('/admin/publication-vault')}
              />
            ) : publication.source_format === 'pdf_upload' ? (
              <PdfPageReader publicationId={publication.id} pageCount={publication.page_image_count || 0} />
            ) : publication.sections && publication.sections.length > 0 ? (
              <PublicationDesignRenderer publication={publication} />
            ) : (
              <LegacyPublicationContent publication={publication} />
            )}
          </div>
        </div>
      </div>

      <style>{`
        /* Disable text selection */
        body {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        
        /* Prevent drag-drop of images */
        img {
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
          user-drag: none;
          pointer-events: none;
        }
      `}</style>
    </Layout>
  );
}

// Agreement gate shown before viewing any publication (public reader only)
function PublicationAgreementGate({ title, onAgree, onDecline }) {
  return (
    <div data-testid="publication-agreement-gate" className="bg-white rounded-2xl shadow-2xl border-t-4 border-[#B5813B] p-8 md:p-12 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Lock className="w-6 h-6 text-[#B5813B]" />
        <h2 className="text-2xl font-bold text-[#1B4B5A]" style={{ fontFamily: 'Georgia, serif' }}>
          Before you continue
        </h2>
      </div>
      {title && (
        <p className="text-gray-700 mb-4">
          You are about to view <span className="font-semibold text-[#1B4B5A]">"{title}"</span>.
        </p>
      )}
      <div className="bg-red-50 border-2 border-red-400 rounded-lg p-5 mb-6">
        <p className="text-gray-800 leading-relaxed">
          This material is for personal viewing only. Unauthorized reproduction, distribution, or
          copying is strictly prohibited. © {new Date().getFullYear()} AcuErnie. All rights reserved.
        </p>
      </div>
      <p className="text-gray-700 mb-6 font-medium">
        By clicking "I Agree" you confirm that you have read and accept these terms.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          data-testid="agreement-agree-button"
          onClick={onAgree}
          className="flex-1 bg-[#1B4B5A] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#143a45] transition-all"
        >
          I Agree
        </button>
        <button
          data-testid="agreement-decline-button"
          onClick={onDecline}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-all"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Legacy renderer for older (chapter/paragraph based) publications
function LegacyPublicationContent({ publication }) {
  return (
    <>
      <div className="bg-gradient-to-br from-white via-[#FFF8F0] to-white rounded-2xl shadow-2xl p-12 mb-12 border-t-4 border-[#1B4B5A]">
        <div className="text-sm uppercase tracking-widest text-[#1B4B5A] mb-4 font-semibold" style={{ userSelect: 'none' }}>
          Featured Publication
        </div>
        <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6 leading-tight" style={{ userSelect: 'none', fontFamily: 'Georgia, serif' }}>
          {publication.title}
        </h1>
        <div className="flex items-center gap-4 text-lg text-gray-700 mb-2" style={{ userSelect: 'none' }}>
          <div className="w-12 h-1 bg-[#B5813B]"></div>
          <span>By <span className="font-semibold text-[#1B4B5A]">{publication.author}</span></span>
        </div>
        {publication.affiliation && (
          <div className="text-base text-gray-600 ml-16 mb-4 italic" style={{ userSelect: 'none' }}>
            {publication.affiliation}
          </div>
        )}
        {publication.description && (
          <div className="text-lg text-gray-700 mt-8 pt-8 border-t-2 border-[#B5813B]/30 leading-relaxed" style={{ userSelect: 'none' }}>
            {publication.description}
          </div>
        )}
      </div>

      <div className="space-y-10">
        {publication.chapters?.map((chapter, chapterIdx) => (
          <div key={chapter.id} className="bg-white rounded-2xl shadow-xl p-10 md:p-12 border-l-4 border-[#B5813B]">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-10 pb-6 border-b-2 border-[#1B4B5A]/20 relative" style={{ userSelect: 'none', fontFamily: 'Georgia, serif' }}>
              <span className="text-[#B5813B] text-5xl font-bold mr-4 opacity-30">{chapterIdx + 1}</span>
              {chapter.title || `Chapter ${chapterIdx + 1}`}
            </h2>
            <div className="space-y-6">
              {chapter.paragraphs?.map((para, paraIdx) => {
                const isFirstParagraph = paraIdx === 0;
                const firstLetter = para.content?.charAt(0) || '';
                const restOfContent = para.content?.substring(1) || '';
                return (
                  <div key={para.id} className="prose prose-lg max-w-none">
                    <p
                      className={`text-gray-800 leading-relaxed text-justify ${isFirstParagraph ? 'first-paragraph-with-dropcap' : ''}`}
                      style={{ userSelect: 'none', fontSize: '1.125rem', lineHeight: '1.875rem' }}
                    >
                      {isFirstParagraph ? (
                        <>
                          <span className="float-left text-6xl font-serif font-bold leading-none mr-2 mt-1 text-[#B5813B] drop-shadow-sm" style={{ lineHeight: '0.8' }}>
                            {firstLetter}
                          </span>
                          {restOfContent}
                        </>
                      ) : (
                        para.content
                      )}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 bg-red-50 border-4 border-red-500 p-8 rounded-lg">
        <p className="text-center text-gray-800 font-semibold" style={{ userSelect: 'none' }}>
          © {new Date().getFullYear()} AcuErnie. All rights reserved. This publication is for view only.
          Unauthorized reproduction, distribution, or copying is strictly prohibited.
        </p>
      </div>
    </>
  );
}
